import { logger } from '@app/logger';

import { type User } from '@services/UsersService';

const EMPTY_USER: Partial<User> = Object.freeze({});

/**
 * True when `value` can be the stored user: an object, not null and not an
 * array.
 *
 * `POST /auth` and `POST /profile` always answer with a user, but a client
 * path can still hand over nothing. One known path: SWR's `trigger()` resolves
 * `undefined` instead of throwing when its request fails after a newer
 * `trigger()` of the same hook has started.
 */
export const isStorableUser = (value: unknown): value is User =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const describeValue = (value: unknown) => {
  if (value === null) {
    return 'null';
  }

  return Array.isArray(value) ? 'array' : typeof value;
};

/**
 * Reports one value that was refused as the stored user. `source` names the
 * caller, and the Error's stack shows the path to it. The value itself is
 * never sent, only its type.
 */
export const reportRefusedUser = (source: string, value: unknown) => {
  logger.captureException(
    new Error(`Refused to store an empty user (${source})`),
    {
      level: 'warning',
      tags: { section: 'auth-store', source },
      extra: { received: describeValue(value) },
    },
  );
};

/**
 * The stored user as a screen should read it. Any field may be missing (the
 * store holds `{}` until `POST /auth` answers), so the type says so. A missing
 * user reads as one shared empty object, which stays stable in hook
 * dependencies.
 */
export const readStoredUser = (user: User | null | undefined): Partial<User> =>
  isStorableUser(user) ? user : EMPTY_USER;
